import { useEffect, useState, type ReactNode } from "react";
import type { McpAuthKind, McpCredentialMode, McpServerInput } from "@krut/shared";
import { Badge, Button, Dialog, ErrorText, Field, Input, Select, Spinner, Switch } from "./ui.tsx";

/** A server as the API returns it (no secrets). */
export interface McpServer {
  id: string;
  key: string;
  name: string;
  description: string;
  url: string;
  auth: McpAuthKind;
  credentials: McpCredentialMode;
  headerNames: string[];
  oauthScope: string;
  oauthClientId: string;
  hasClientSecret: boolean;
  enabled: boolean;
  company: boolean;
  connection: { status: "none" | "pending" | "connected" | "expired" | "error"; error: string | null; connectedAt: string | null } | null;
}

export const AUTH_LABEL: Record<McpAuthKind, string> = { none: "No sign-in", headers: "API key / headers", oauth: "OAuth sign-in" };

const host = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

export function ConnectionBadge({ s, sharedHint }: { s: McpServer; sharedHint?: string }) {
  if (!s.connection) return <Badge tone="good">ready</Badge>;
  const st = s.connection.status;
  if (st === "connected") return <Badge tone="good">connected</Badge>;
  if (st === "expired") return <Badge tone="bad">sign-in expired</Badge>;
  if (st === "error") return <Badge tone="bad">error</Badge>;
  if (st === "pending") return <Badge>signing in…</Badge>;
  return <Badge>{sharedHint ?? "not connected"}</Badge>;
}

/** One server in a list: name, where it lives, how it signs in; actions on the right. */
export function ServerRow({ s, badge, actions, extra }: { s: McpServer; badge: ReactNode; actions: ReactNode; extra?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[var(--panel-2)] text-sm font-semibold uppercase">{s.name[0]}</div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 font-medium">
          {s.name}
          {!s.enabled && <Badge>off</Badge>}
        </div>
        <div className="truncate text-xs text-[var(--muted)]">
          {host(s.url)} · {AUTH_LABEL[s.auth]}
          {s.company && s.auth !== "none" && (s.credentials === "shared" ? " · shared account" : " · everyone connects their own")}
          {s.description && <> · {s.description}</>}
        </div>
        {s.connection?.error && s.connection.status !== "connected" && <div className="mt-1 text-xs text-red-700">{s.connection.error}</div>}
        {extra}
      </div>
      {badge}
      <div className="flex items-center gap-1">{actions}</div>
    </div>
  );
}

export const emptyServer = (company: boolean): McpServerInput => ({
  name: "",
  description: "",
  url: "",
  auth: "none",
  credentials: company ? "shared" : "personal",
  headerNames: [],
  oauthClientId: "",
  oauthScope: "",
  enabled: true,
});

/**
 * Add or edit a server. For header auth with its own credentials (a person's server, or a shared
 * company one) the header values are asked for here too and saved as the connection.
 */
export function ServerDialog({
  open,
  onClose,
  company,
  initial,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  company: boolean;
  initial: McpServer | null;
  onSave: (input: McpServerInput, headerValues: Record<string, string> | null) => Promise<void>;
}) {
  const [f, setF] = useState<McpServerInput>(emptyServer(company));
  const [headers, setHeaders] = useState<{ name: string; value: string }[]>([{ name: "Authorization", value: "" }]);
  const [secret, setSecret] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSecret("");
    if (initial) {
      const { id: _i, key: _k, company: _c, connection: _cn, hasClientSecret: _h, ...rest } = initial;
      setF(rest);
      setHeaders(initial.headerNames.length ? initial.headerNames.map((name) => ({ name, value: "" })) : [{ name: "Authorization", value: "" }]);
      setAdvanced(!!initial.oauthClientId || !!initial.oauthScope);
    } else {
      setF(emptyServer(company));
      setHeaders([{ name: "Authorization", value: "" }]);
      setAdvanced(false);
    }
  }, [open, initial, company]);

  const set = (p: Partial<McpServerInput>) => setF((x) => ({ ...x, ...p }));
  // Values are asked for when this dialog's user is also the one whose credentials they are.
  const ownCredentials = !company || f.credentials === "shared";
  const editing = !!initial;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const named = headers.filter((h) => h.name.trim());
      const input: McpServerInput = {
        ...f,
        headerNames: f.auth === "headers" ? named.map((h) => h.name.trim()) : [],
        ...(secret && { oauthClientSecret: secret }),
      };
      const values = f.auth === "headers" && ownCredentials && named.every((h) => h.value) && named.length ? Object.fromEntries(named.map((h) => [h.name.trim(), h.value])) : null;
      if (f.auth === "headers" && ownCredentials && !editing && !values) throw new Error("Fill in the header values.");
      await onSave(input, values);
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={editing ? `Edit ${initial!.name}` : company ? "Add a company MCP server" : "Add an MCP server"} wide>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name">
            <Input required maxLength={60} value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="Linear" />
          </Field>
          <Field label="Server URL" hint={company ? "Streamable HTTP endpoint, often ending in /mcp. Internal addresses are allowed." : "Streamable HTTP endpoint (https), often ending in /mcp."}>
            <Input required type="url" value={f.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://mcp.linear.app/mcp" />
          </Field>
        </div>
        <Field label="Description" hint="Optional: what it's for.">
          <Input maxLength={300} value={f.description} onChange={(e) => set({ description: e.target.value })} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Sign-in">
            <Select value={f.auth} onChange={(e) => set({ auth: e.target.value as McpAuthKind })}>
              <option value="none">None (public server)</option>
              <option value="headers">API key / headers</option>
              <option value="oauth">OAuth sign-in</option>
            </Select>
          </Field>
          {company && f.auth !== "none" && (
            <Field label="Whose account" hint={f.credentials === "shared" ? "You sign in once; every assistant uses that account." : "Everyone connects their own account before their assistant can use it."}>
              <Select value={f.credentials} onChange={(e) => set({ credentials: e.target.value as McpCredentialMode })}>
                <option value="shared">One shared account (yours)</option>
                <option value="personal">Each person's own</option>
              </Select>
            </Field>
          )}
        </div>

        {f.auth === "headers" && (
          <div className="space-y-2 rounded-lg border border-[var(--line)] p-4">
            <div className="text-sm font-medium">Headers</div>
            <p className="text-xs text-[var(--muted)]">
              {ownCredentials
                ? editing
                  ? "Leave the values empty to keep the saved ones. They're stored encrypted and never shown again."
                  : "Stored encrypted; the assistant never sees them. For a bearer key use Authorization = Bearer <key>."
                : "Just the header names: each person fills in their own values when they connect."}
            </p>
            {headers.map((h, i) => (
              <div key={i} className="flex gap-2">
                <Input className="max-w-[40%]" value={h.name} onChange={(e) => setHeaders(headers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Authorization" />
                {ownCredentials && (
                  <Input type="password" autoComplete="off" value={h.value} onChange={(e) => setHeaders(headers.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} placeholder={editing ? "unchanged" : "Bearer sk-…"} />
                )}
                {headers.length > 1 && (
                  <Button type="button" variant="ghost" onClick={() => setHeaders(headers.filter((_, j) => j !== i))}>Remove</Button>
                )}
              </div>
            ))}
            {headers.length < 5 && (
              <button type="button" className="text-sm text-[var(--color-accent)] hover:underline" onClick={() => setHeaders([...headers, { name: "", value: "" }])}>
                Add a header
              </button>
            )}
          </div>
        )}

        {f.auth === "oauth" && (
          <div className="space-y-3 rounded-lg border border-[var(--line)] p-4 text-sm">
            <p className="text-xs text-[var(--muted)]">
              {ownCredentials ? "After saving you'll be sent to the server to sign in." : "Everyone signs in with their own account from their MCP servers page."} Most servers register this app automatically; some (like
              GitHub) need an OAuth app you create there.
            </p>
            <button type="button" className="text-sm text-[var(--color-accent)] hover:underline" onClick={() => setAdvanced(!advanced)}>
              {advanced ? "Hide" : "Show"} OAuth client settings
            </button>
            {advanced && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Client ID" hint="Only if the server doesn't support automatic registration.">
                  <Input value={f.oauthClientId} onChange={(e) => set({ oauthClientId: e.target.value })} />
                </Field>
                <Field label="Client secret" hint={initial?.hasClientSecret ? "Saved. Type to replace." : "If the OAuth app has one."}>
                  <Input type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} />
                </Field>
                <Field label="Scopes" hint="Space-separated; empty = the server's default.">
                  <Input value={f.oauthScope} onChange={(e) => set({ oauthScope: e.target.value })} />
                </Field>
                <Field label="Redirect URL" hint="Register this in the OAuth app.">
                  <Input readOnly value={`${location.origin}/api/mcp/oauth/callback`} onFocus={(e) => e.target.select()} />
                </Field>
              </div>
            )}
          </div>
        )}

        <Switch checked={f.enabled} onChange={(v) => set({ enabled: v })} label="Enabled" hint={company ? "Off: hidden from every assistant." : "Off: your assistant doesn't use it."} />
        <p className="text-xs text-[var(--muted)]">{company ? "Adding, removing or switching a server restarts assistants briefly so they pick it up." : "Adding, removing or switching a server restarts your assistant briefly."}</p>
        <ErrorText error={error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy}>{busy ? <Spinner /> : editing ? "Save" : "Add server"}</Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Header values for a server that uses each person's own credentials. */
export function HeadersDialog({ server, onClose, onSubmit }: { server: McpServer | null; onClose: () => void; onSubmit: (headers: Record<string, string>) => Promise<void> }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    setValues({});
    setError(null);
  }, [server]);
  return (
    <Dialog open={!!server} onClose={onClose} title={`Connect ${server?.name ?? ""}`}>
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await onSubmit(values);
            onClose();
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className="text-sm text-[var(--muted)]">Stored encrypted and used only for your assistant. It never sees the values.</p>
        {server?.headerNames.map((n) => (
          <Field key={n} label={n}>
            <Input required type="password" autoComplete="off" value={values[n] ?? ""} onChange={(e) => setValues({ ...values, [n]: e.target.value })} placeholder={n.toLowerCase() === "authorization" ? "Bearer …" : ""} />
          </Field>
        ))}
        <ErrorText error={error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy}>{busy ? <Spinner /> : "Connect"}</Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Connects to a server and lists its tools: the quickest way to see that it works. */
export function ToolsDialog({ server, onClose, run }: { server: McpServer | null; onClose: () => void; run: (id: string) => Promise<{ tools: { name: string; description: string }[] }> }) {
  const [state, setState] = useState<{ tools?: { name: string; description: string }[]; error?: unknown }>({});
  useEffect(() => {
    if (!server) return;
    setState({});
    run(server.id).then((r) => setState({ tools: r.tools }), (e) => setState({ error: e }));
  }, [server, run]);
  return (
    <Dialog open={!!server} onClose={onClose} title={`${server?.name ?? ""}: tools`} wide>
      {!state.tools && !state.error && (
        <div className="flex items-center gap-3 py-6 text-sm text-[var(--muted)]"><Spinner /> Connecting…</div>
      )}
      <ErrorText error={state.error} />
      {state.tools && (
        <>
          <p className="mb-3 text-sm text-[var(--muted)]">
            Connected. {state.tools.length} tool{state.tools.length === 1 ? "" : "s"}; the assistant sees them as <code className="text-xs">mcp_{server?.key}_…</code>
          </p>
          <div className="max-h-[50vh] divide-y divide-[var(--line)] overflow-y-auto rounded-lg border border-[var(--line)]">
            {state.tools.map((t) => (
              <div key={t.name} className="px-4 py-2.5">
                <div className="font-mono text-sm">{t.name}</div>
                {t.description && <div className="line-clamp-2 text-xs text-[var(--muted)]">{t.description}</div>}
              </div>
            ))}
          </div>
        </>
      )}
      <div className="mt-4 flex justify-end">
        <Button onClick={onClose}>Close</Button>
      </div>
    </Dialog>
  );
}

/** ?connected=… / ?error=… after an OAuth round trip; cleared from the address bar. */
export function useOAuthResult() {
  const [result] = useState(() => {
    const q = new URLSearchParams(location.search);
    return { connected: q.get("connected"), error: q.get("error") };
  });
  useEffect(() => {
    if (result.connected || result.error) history.replaceState(null, "", location.pathname);
  }, [result]);
  return result;
}
